import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  ApiException,
  BatteryFilterType,
  CreateOrUpdateProjectVersionCommand,
  CreateProjectCommand,
  DatasetPageClient,
  IDatasetPageClient,
  IProjectsClient,
  ProjectDto,
  ProjectsClient,
  UpdateProjectCommand,
} from "app/ApiClient";
import { LoadingState, ProjectDataViewModes } from "types";

import { IState } from "..";
import { NormalizedOriginProject } from "../originProjects/originProjectState";
import { initialProjectState, NormalizedProjectSettings, ProjectState } from "./projectState";
import { EncodingVersionDto } from "app/SharedApiClient";

const projectsClient: IProjectsClient = new ProjectsClient(process.env.REACT_APP_API_URL);
const datasetsClient: IDatasetPageClient = new DatasetPageClient(process.env.REACT_APP_API_URL);

export const updateProjectLatestEncodingVersion = createAsyncThunk(
  "projects/updateProjectLatestEncodingVersion",
  async (_, { getState, rejectWithValue }) => {
    const {
      project: { currentProject },
    } = getState() as IState;
    const projectId = currentProject?.id;
    if (!projectId) {
      return null;
    }

    try {
      await projectsClient.updateProjectLatestVersion(projectId);
      const project = await projectsClient.getProjectById(projectId);
      return project;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async ({ command }: { command: UpdateProjectCommand; originProjectId?: string }, { rejectWithValue }) => {
    try {
      await projectsClient.updateProject(command);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (projectId: number, { rejectWithValue }) => {
    try {
      await projectsClient.deleteProject(projectId);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getProjectById = createAsyncThunk("projects/getById", async (id: number, { rejectWithValue }) => {
  try {
    const project = await projectsClient.getProjectById(id);
    return project;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const extractProjectNameFromLabel = (originProjectName: string | undefined, originProjectId: string): string => {
  if (originProjectName === undefined) {
    return originProjectId;
  }
  if (originProjectName.includes(originProjectId)) {
    let startIndex = originProjectName.indexOf(originProjectId) + originProjectId.length + 1;
    
    while(true){
      const letter = originProjectName.charAt(startIndex);
      if(letter === ' ' || letter === '-'){
        startIndex++;
      }else{
        break;
      }
    }
    
    const name = originProjectName.substring(startIndex);

    return name === "" ? originProjectId : name;
  }
  return originProjectName.length >= originProjectId.length ? originProjectName : originProjectId;
};

export const createProject = createAsyncThunk("projects/createProject", async (data: CreateProjectCommand) => {
  try {
    const actualName = extractProjectNameFromLabel(data.originProjectName, data.originProjectId);
    const newData = { ...data, originProjectName: actualName };
    const response = await projectsClient.createProject(newData);
    return response;
  } catch (error) {
    const { message } = error as Error;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { response, result } = error as ApiException;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument
    throw new Error(result?.errorMessage ?? response ?? message);
  }
});

export const checkDimensionProjectExists = createAsyncThunk(
  "projects/checkDimensionProjectExists",
  async (dimensionProjectId: string, { rejectWithValue, signal }) => {
    try {
      const existingProject = await projectsClient.getProjectByOriginProjectId(dimensionProjectId);
      if (signal.aborted) {
        rejectWithValue(null);
      }
      return existingProject;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getProjectSettings = createAsyncThunk(
  "projects/getSettings",
  async (
    {
      encodedVersionId,
      projectId,
      stackId,
      modificationId,
    }: {
      encodedVersionId: string;
      projectId: number;
      stackId?: number;
      modificationId?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await projectsClient.getSettings(projectId, encodedVersionId, stackId, modificationId);
      if(!response) {
        return null;
      }
      const result = response as NormalizedProjectSettings;
      let parsedBatteries: string[] = [];
      if (response.checkedBatteries) {
        parsedBatteries = JSON.parse(response.checkedBatteries) as string[];
      }
      result.normalizedCheckedBatteries = parsedBatteries;

      let parsedExcludedVariables: string[] = [];
      if (response.excludedVariables) {
        parsedExcludedVariables = JSON.parse(response.excludedVariables) as string[];
      }
      result.normalizedExcludedVariables = parsedExcludedVariables;

      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const getPrevEncodedVersionId = (encodedVersions:EncodingVersionDto[]) => {
    const sortedEncodedVersions = [...encodedVersions].sort((a, b)=>{
      const dateA = new Date(a.versionDate).getTime();
      const dateB = new Date(b.versionDate).getTime();
      return dateB - dateA;
    })
    return sortedEncodedVersions[1].versionId;
}

export const updateProjectSettings = createAsyncThunk(
  "projects/updateProjectSettings",
  async (
    {
      projectId,
      encodedVersionId,
      batteries,
      excludedVariables,
      batteryFilterTypes,
      stackId,
      modificationId,
    }: {
      projectId: number;
      encodedVersionId: string;
      batteries: string[];
      excludedVariables: string[];
      batteryFilterTypes: BatteryFilterType[];
      stackId?: number;
      modificationId?: number;
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const { availableVersions, encodingTriggeredHistory } = (getState() as IState).projectEncodingVersion;
      let prevEncodedVersionId = encodedVersionId;
      
      if(availableVersions && availableVersions.length > 1) {
        prevEncodedVersionId = getPrevEncodedVersionId(availableVersions);
      }

      const isStacked = (encodingTriggeredHistory && encodingTriggeredHistory.isStacked) ?? false;

      await projectsClient.updateSettings({
        projectId,
        encodedVersionId,
        prevEncodedVersionId: prevEncodedVersionId,
        stackId,
        checkedBatteries: JSON.stringify(batteries),
        excludedVariables: JSON.stringify(excludedVariables),
        batteryFilterTypes,
        modificationId,
        isStacked
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addOrUpdateProjectSettingsExcludedVariables = createAsyncThunk(
  "projects/addOrUpdateProjectSettingsExcludedVariables",
  async (
    {
      projectId,
      encodedVersionId,
      batteries,
      excludedVariables,
      batteryFilterTypes,
      stackId,
      modificationId,
    }: {
      projectId: number;
      encodedVersionId: string;
      batteries: string[];
      excludedVariables: string[];
      batteryFilterTypes: BatteryFilterType[];
      stackId?: number;
      modificationId?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      await datasetsClient.addOrUpdateExcludedDatasetsSettings({
        projectId,
        encodedVersionId,
        stackId,
        checkedBatteries: JSON.stringify(batteries),
        excludedVariables: JSON.stringify(excludedVariables),
        batteryFilterTypes,
        modificationId,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removeProjectSettingsExcludedVariables = createAsyncThunk(
  "projects/removeProjectSettingsExcludedVariables",
  async (
    {
      projectId,
      encodedVersionId,
      batteries,
      excludedVariables,
      batteryFilterTypes,
      stackId,
      modificationId,
    }: {
      projectId: number;
      encodedVersionId: string;
      batteries: string[];
      excludedVariables: string[];
      batteryFilterTypes: BatteryFilterType[];
      stackId?: number;
      modificationId?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      await datasetsClient.removeExcludedDatasetsSettings({
        projectId,
        encodedVersionId,
        stackId,
        checkedBatteries: JSON.stringify(batteries),
        excludedVariables: JSON.stringify(excludedVariables),
        batteryFilterTypes,
        modificationId,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createOrUpdateProjectVersion = createAsyncThunk(
  "projects/createOrUpdateProjectVersion",
  async (query: CreateOrUpdateProjectVersionCommand, { rejectWithValue }) => {
    try {
      const result = await projectsClient.createOrUpdateProjectVersion(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getProjectVersions = createAsyncThunk(
  "projects/getProjectVersions",
  async (id: number, { rejectWithValue }) => {
    try {
      const result = await projectsClient.getProjectVersions(id);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const projectSlice = createSlice({
  name: "project",
  initialState: initialProjectState,
  reducers: {
    toggleDVFilterSetting:(state) => {
      state.isDvFilterSelected = !state.isDvFilterSelected;
    },
    toggleRespondablesFilterSetting:(state) => {
      state.isRespondablesFilterSelected = !state.isRespondablesFilterSelected;
    },
    setProjectViewMode: (state, { payload }: PayloadAction<ProjectDataViewModes>) => {
      state.viewMode = payload;
    },
    resetProject: () => initialProjectState,

    resetDeleteProjectLoadingState: (state) => {
      state.deleteProjectLoadingState = LoadingState.idle;
      state.deleteProjectError = "";
    },
    resetUpdateProjectLatestEncodingVersionLoadingState: (state) => {
      state.updateProjectLatestEncodingVersionLoadingState = LoadingState.idle;
      state.updateProjectLatestEncodingVersionError = "";
    },
    setAddProjectDialogOpenedState: (state, { payload }: { payload: boolean }) => {
      state.isAddProjectDialogOpened = payload;
      // reset errors and existingProjectId
      state.createProjectError = undefined;
      state.existingProject = undefined;
    },
    setAddCompositeProjectDialogOpenedState: (state, { payload }: { payload: boolean }) => {
      state.isAddCompositeProjectDialogOpened = payload;
      state.createProjectError = undefined;
      state.existingProject = undefined;
    },
    setSelectedOriginProject: (state, { payload }: { payload: NormalizedOriginProject | null }) => {
      state.selectedOriginProject = payload;
    },
    setOpenCreateCompositeProject: (state, { payload }: { payload: boolean }) => {
      state.isOpenCreateCompositeProject = payload;
      state.createProjectError = undefined;
      state.existingProject = undefined;
    },
    setOpenCreateCustomDataProject: (state, { payload }: { payload: boolean }) => {
      state.isOpenCreateCustomDataProject = payload;
      state.createProjectError = undefined;
      state.existingProject = undefined;
    },
    setOpenCreateWalrDataProject: (state, { payload }: { payload: boolean }) => {
      state.isOpenCreateWalrDataProject = payload;
      state.createProjectError = undefined;
      state.existingProject = undefined;
    },
    updateCurrentProjectName : (state, { payload: { projectId, projectName } }: {payload: {projectId: number, projectName: string}})=>{
      if(state.currentProject && state.currentProject.id === projectId){
        state.currentProject.name = projectName;
      }
    }
  },
  extraReducers: (builder) => {
    // getProjectById
    builder.addCase(getProjectById.pending, (state) => {
      state.getProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(getProjectById.fulfilled, (state, action) => {
      state.getProjectLoadingState = LoadingState.succeed;
      state.currentProject = action.payload;
      state.status = action.payload.status;
    });
    builder.addCase(getProjectById.rejected, (state, action) => {
      state.getProjectLoadingState = LoadingState.pending;
      state.getProjectError = action.error.message;
    });

    // create project
    builder.addCase(createProject.pending, (state) => {
      state.createProjectLoadingState = LoadingState.pending;
      state.createProjectError = undefined;
    });
    builder.addCase(createProject.fulfilled, (state, action) => {
      state.createProjectLoadingState = LoadingState.succeed;
      state.createdProject = action.payload;
    });
    builder.addCase(createProject.rejected, (state, action) => {
      state.createProjectLoadingState = LoadingState.failed;
      state.createProjectError = action.error.message;
    });
    // checkDimensionProjectExists
    builder.addCase(checkDimensionProjectExists.pending, (state) => {
      state.existingProject = undefined;
      state.createProjectError = undefined;
    });
    builder.addCase(checkDimensionProjectExists.fulfilled, (state, action) => {
      state.existingProject = action.payload;
    });
    builder.addCase(checkDimensionProjectExists.rejected, (state) => {
      state.existingProject = undefined;
    });

    // updateProjectToLatestEncodingVersion
    builder.addCase(updateProjectLatestEncodingVersion.pending, (state) => {
      state.updateProjectLatestEncodingVersionLoadingState = LoadingState.pending;
    });
    builder.addCase(updateProjectLatestEncodingVersion.fulfilled, (state, action) => {
      state.updateProjectLatestEncodingVersionLoadingState = LoadingState.succeed;
      state.currentProject = action.payload;
    });
    builder.addCase(updateProjectLatestEncodingVersion.rejected, (state, action) => {
      state.updateProjectLatestEncodingVersionLoadingState = LoadingState.pending;
      state.updateProjectLatestEncodingVersionError = action.error.message;
    });

    // deleteProject
    builder.addCase(deleteProject.pending, (state) => {
      state.deleteProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(deleteProject.fulfilled, (state) => {
      state.deleteProjectLoadingState = LoadingState.succeed;
    });
    builder.addCase(deleteProject.rejected, (state, action) => {
      state.deleteProjectLoadingState = LoadingState.pending;
      state.deleteProjectError = action.error.message;
    });

    // updateProject
    builder.addCase(updateProject.pending, (state) => {
      state.updateProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(updateProject.fulfilled, (state, { meta: { arg } }) => {
      state.updateProjectLoadingState = LoadingState.succeed;

      if (arg.command.encodedVersionId !== state.currentProject?.versionId) {
        const versionDate = arg?.command.encodedVersionDate;
        const versionId = arg?.command.encodedVersionId;

        const updatedProject: ProjectDto = {
          ...state.currentProject,
          versionDate,
          versionId,
        };
        state.currentProject = updatedProject;
      }
    });
    builder.addCase(updateProject.rejected, (state, action) => {
      state.updateProjectLoadingState = LoadingState.pending;
      state.updateProjectError = action.error.message;
    });

    // getProjectSettings
    builder.addCase(getProjectSettings.rejected, (state) => {
      state.projectSettings = null;
      state.projectSettingsLoadingState = LoadingState.failed;
    });
    builder.addCase(getProjectSettings.pending, (state) => {
      state.projectSettings = null;
      state.projectSettingsLoadingState = LoadingState.pending;
    });
    builder.addCase(getProjectSettings.fulfilled, (state, action) => {
      state.projectSettings = action.payload;
      state.projectSettingsLoadingState = LoadingState.succeed;
      state.isDvFilterSelected = (state.projectSettings?.batteryFilterTypes && state.projectSettings.batteryFilterTypes.some(item=>item===BatteryFilterType.DV)) ?? false;
      state.isRespondablesFilterSelected = (state.projectSettings?.batteryFilterTypes && state.projectSettings.batteryFilterTypes.some(item=>item===BatteryFilterType.Respondables)) ?? false;
      state.isStacked = state.projectSettings?.isStacked ?? false;
    });

    // updateProjectSettings
    builder.addCase(updateProjectSettings.pending, (state) => {
      state.updateProjectSettingsState = LoadingState.pending;
    });
    builder.addCase(updateProjectSettings.fulfilled, (state) => {
      state.updateProjectSettingsState = LoadingState.succeed;
    });
    builder.addCase(updateProjectSettings.rejected, (state) => {
      state.updateProjectSettingsState = LoadingState.failed;
    });

    // addOrUpdateProjectSettingsExcludedVariables
    builder.addCase(addOrUpdateProjectSettingsExcludedVariables.pending, (state) => {
      state.updateProjectSettingsState = LoadingState.pending;
    });
    builder.addCase(addOrUpdateProjectSettingsExcludedVariables.fulfilled, (state) => {
      state.updateProjectSettingsState = LoadingState.succeed;
    });
    builder.addCase(addOrUpdateProjectSettingsExcludedVariables.rejected, (state) => {
      state.updateProjectSettingsState = LoadingState.failed;
    });

    // removeProjectSettingsExcludedVariables
    builder.addCase(removeProjectSettingsExcludedVariables.pending, (state) => {
      state.updateProjectSettingsState = LoadingState.pending;
    });
    builder.addCase(removeProjectSettingsExcludedVariables.fulfilled, (state) => {
      state.updateProjectSettingsState = LoadingState.succeed;
    });
    builder.addCase(removeProjectSettingsExcludedVariables.rejected, (state) => {
      state.updateProjectSettingsState = LoadingState.failed;
    });

    //createOrUpdateProjectVersion
    builder.addCase(createOrUpdateProjectVersion.fulfilled, (state, { payload }) => {
      if (state.currentProject) {
        state.projectVersions = state.projectVersions
          ?.filter((v) => v.encodedVersionId !== payload.encodedVersionId)
          .concat(payload) ?? [payload];
      }
    });

    //getProjectVersions
    builder.addCase(getProjectVersions.fulfilled, (state, { payload }) => {
      state.projectVersions = payload;
    });
    builder.addCase(getProjectVersions.rejected, (state) => {
      state.projectVersions = [];
    });
    builder.addCase(getProjectVersions.pending, (state) => {
      state.projectVersions = [];
    });
  },
});

export const {
  resetProject,
  setSelectedOriginProject,
  setAddProjectDialogOpenedState,
  setAddCompositeProjectDialogOpenedState,
  resetDeleteProjectLoadingState,
  resetUpdateProjectLatestEncodingVersionLoadingState,
  setProjectViewMode,
  setOpenCreateCompositeProject, 
  setOpenCreateCustomDataProject,
  setOpenCreateWalrDataProject,
  updateCurrentProjectName,
  toggleDVFilterSetting,
  toggleRespondablesFilterSetting,
} = projectSlice.actions;

export const projectSelector = (state: RootState): ProjectState => state.project;

export default projectSlice.reducer;
